import 'svelte/internal/disclose-version';
import * as $ from 'svelte/internal/client';
import { createChallenge, verifyChallenge } from '$lib/api/captcha';
import { saveToken } from '$lib/stores/captchaStore';
import Button from '$lib/components/ui/button/button.svelte';
import { RefreshCw } from '$lib/icons';

var root_2 = $.from_html(`<div class="flex items-center justify-center rounded border bg-muted w-full" style="height: 160px;" aria-label="Loading challenge"><svg class="animate-spin h-6 w-6 text-muted-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg></div>`);
var root_4 = $.from_html(`<img alt="Math addition question — click the correct answer" class="rounded border cursor-pointer w-full max-w-[400px] select-none" draggable="false"/>`);
var root_5 = $.from_html(`<p class="text-sm text-destructive" role="alert"> </p>`);
var root_6 = $.from_html(`<!> New challenge`, 1);
var root_7 = $.from_html(`<p class="text-xs text-muted-foreground font-mono"> </p>`);
var root_1 = $.from_html(`<div class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="captcha-modal-title" tabindex="-1"><div class="bg-background rounded-lg shadow-lg w-full max-w-[460px] animate-in fade-in zoom-in-95"><div class="p-6 pb-3"><h2 id="captcha-modal-title" class="text-lg font-semibold">Human Verification</h2> <p class="text-sm text-muted-foreground mt-1">Click the box showing the correct answer to continue.</p></div> <div class="px-6 pb-2 flex flex-col items-center gap-3"><!> <!></div> <div class="px-6 pb-6 pt-2 flex flex-col items-center gap-1"><!> <!></div></div></div>`);

export default function CaptchaModal($$anchor, $$props) {
	$.push($$props, true);

	/**
	 * CaptchaModal — human verification dialog.
	 * Loads a math-addition CAPTCHA from the server (encrypted token + inline base64 image).
	 * User must click the correct answer area; on success a signed token is stored
	 * in localStorage and onSuccess() is called.
	 * The dialog cannot be dismissed without solving the challenge.
	 */
	let challengeToken = $.state(null);

	let imageBase64 = $.state(null);
	let secretFingerprint = $.state(null);
	let loading = $.state(false);
	let errorMessage = $.state(null);
	let imgElement = $.state(null);

	async function loadChallenge() {
		$.set(loading, true);
		$.set(challengeToken, null);
		$.set(imageBase64, null);

		try {
			const response = await createChallenge();

			$.set(challengeToken, response.challengeToken, true);
			$.set(imageBase64, response.imageBase64, true);
			$.set(secretFingerprint, response.secretFingerprint, true);
			$.set(errorMessage, null);
		} catch {
			$.set(errorMessage, 'Failed to load the verification challenge. Please try again.');
		} finally {
			$.set(loading, false);
		}
	}

	$.user_effect(() => {
		if ($$props.open && !$.get(challengeToken) && !$.get(loading)) {
			loadChallenge();
		}
	});

	async function handleImageClick(event) {
		if (!$.get(challengeToken) || !$.get(imgElement) || $.get(loading)) return;

		// Scale CSS pixel coordinates to actual image pixel coordinates
		const rect = $.get(imgElement).getBoundingClientRect();

		const scaleX = $.get(imgElement).naturalWidth / rect.width;
		const scaleY = $.get(imgElement).naturalHeight / rect.height;
		const x = Math.round((event.clientX - rect.left) * scaleX);
		const y = Math.round((event.clientY - rect.top) * scaleY);

		try {
			const response = await verifyChallenge($.get(challengeToken), x, y);

			saveToken(response.token);
			$.set(challengeToken, null);
			$.set(imageBase64, null);
			$$props.onSuccess();
		} catch {
			$.set(errorMessage, 'Wrong answer — try again!');
			await loadChallenge();
		}
	}

	var fragment = $.comment();
	var node = $.first_child(fragment);

	{
		var consequent_4 = ($$anchor) => {
			var div = root_1();
			var div_1 = $.child(div);
			var div_2 = $.sibling($.child(div_1), 2);
			var node_1 = $.child(div_2);

			{
				var consequent = ($$anchor) => {
					var div_3 = root_2();

					$.append($$anchor, div_3);
				};

				var alternate = ($$anchor) => {
					var fragment_1 = $.comment();
					var node_2 = $.first_child(fragment_1);

					{
						var consequent_1 = ($$anchor) => {
							var img = root_4();

							img.__click = handleImageClick;
							$.bind_this(img, ($$value) => $.set(imgElement, $$value), () => $.get(imgElement));
							$.template_effect(() => $.set_attribute(img, 'src', `data:image/png;base64,${$.get(imageBase64) ?? ''}`));
							$.append($$anchor, img);
						};

						$.if(
							node_2,
							($$render) => {
								if ($.get(imageBase64)) $$render(consequent_1);
							},
							true
						);
					}

					$.append($$anchor, fragment_1);
				};

				$.if(node_1, ($$render) => {
					if ($.get(loading)) $$render(consequent); else $$render(alternate, false);
				});
			}

			var node_3 = $.sibling(node_1, 2);

			{
				var consequent_2 = ($$anchor) => {
					var p = root_5();
					var text = $.child(p, true);

					$.reset(p);
					$.template_effect(() => $.set_text(text, $.get(errorMessage)));
					$.append($$anchor, p);
				};

				$.if(node_3, ($$render) => {
					if ($.get(errorMessage)) $$render(consequent_2);
				});
			}

			$.reset(div_2);

			var div_4 = $.sibling(div_2, 2);
			var node_4 = $.child(div_4);

			Button(node_4, {
				variant: 'ghost',
				size: 'sm',
				onclick: loadChallenge,

				get disabled() {
					return $.get(loading);
				},

				class: 'min-h-[44px] gap-2',

				children: ($$anchor, $$slotProps) => {
					var fragment_2 = root_6();
					var node_5 = $.first_child(fragment_2);

					RefreshCw(node_5, { class: 'h-4 w-4' });
					$.next();
					$.append($$anchor, fragment_2);
				},

				$$slots: { default: true }
			});

			var node_6 = $.sibling(node_4, 2);

			{
				var consequent_3 = ($$anchor) => {
					var p_1 = root_7();
					var text_1 = $.child(p_1);

					$.reset(p_1);
					$.template_effect(() => $.set_text(text_1, `key: ${$.get(secretFingerprint) ?? ''}`));
					$.append($$anchor, p_1);
				};

				$.if(node_6, ($$render) => {
					if ($.get(secretFingerprint)) $$render(consequent_3);
				});
			}

			$.reset(div_4);
			$.reset(div_1);
			$.reset(div);
			$.append($$anchor, div);
		};

		$.if(node, ($$render) => {
			if ($$props.open) $$render(consequent_4);
		});
	}

	$.append($$anchor, fragment);
	$.pop();
}

$.delegate(['click']);
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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